<template>
  <div class="group w-full e1:m-auto e1:max-w-[1136px]">
    <ClientOnly>
      <!-- SP: 縦並び (メイン上、サムネイル下・横スクロール) -->
      <div v-if="isSP">
        <Carousel @click="handleMainCarouselClick" @init-api="(val: any) => (emblaMainApi = val)">
          <CarouselContent>
            <CarouselItem v-for="(imageUrl, index) in imgUrls" :key="index">
              <OptimizedImage
                loading="lazy"
                :alt="`experience-image-${index}`"
                :class="['aspect-[3/2] w-full object-cover object-center']"
                :image-url="imageUrl"
                :optimized-image-options="{ width: 1280 }"
              />
            </CarouselItem>
          </CarouselContent>
          <CarouselDotIndicator />
          <div class="absolute bottom-4 right-4 z-10 flex h-6 w-6 !cursor-pointer items-center justify-center bg-black/40 transition-colors hover:bg-black/60" @click="handleExpandClick">
            <img alt="イメージ拡大マーク" class="h-4 w-4" src="@/assets/icon/expand/ghost.svg" />
          </div>
        </Carousel>
        <Carousel class="pt-[1px]" @init-api="(val: any) => (emblaThumbnailApi = val)">
          <CarouselContent class="ml-0 flex h-fit w-full gap-x-[1px]">
            <CarouselItem v-for="(imageUrl, index) in imgUrls" :key="index" class="h-[65px] shrink-0 basis-auto cursor-pointer pl-0" @click="onThumbClick(index)">
              <OptimizedImage
                loading="lazy"
                :alt="`experience-thumbnail-${index}`"
                :class="['aspect-[3/2] h-full w-auto object-cover object-center', selectedIndex === index ? 'opacity-30' : '']"
                :image-url="imageUrl"
                :optimized-image-options="{ width: 128 }"
              />
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>
      <!-- Desktop: 横並び (メイン左、サムネイル右) -->
      <div v-else class="flex gap-x-[2px]">
        <!-- メイン画像エリア -->
        <div class="relative w-4/5">
          <Carousel ref="desktopCarouselRef" @click="handleMainCarouselClick" @init-api="(val: any) => (emblaMainApi = val)">
            <CarouselContent>
              <CarouselItem v-for="(imageUrl, index) in imgUrls" :key="index">
                <OptimizedImage
                  loading="lazy"
                  :alt="`experience-image-${index}`"
                  :class="['aspect-[3/2] w-full object-cover object-center']"
                  :image-url="imageUrl"
                  :optimized-image-options="{ width: 1280 }"
                />
              </CarouselItem>
            </CarouselContent>
            <CarouselPreviousBase class="hidden group-hover:block" orientation="horizontal" :can-scroll-prev="canScrollPrev" :on-click-scroll-prev="onClickScrollPrev" />
            <CarouselNextBase class="hidden group-hover:block" orientation="horizontal" :can-scroll-next="canScrollNext" :on-click-scroll-next="onClickScrollNext" />
            <CarouselDotIndicator class="hidden group-hover:flex" />
            <div class="absolute bottom-4 right-4 z-10 flex h-8 w-8 !cursor-pointer items-center justify-center bg-black/40 transition-colors hover:bg-black/60" @click="handleExpandClick">
              <img alt="イメージ拡大マーク" class="h-5 w-5" src="@/assets/icon/expand/ghost.svg" />
            </div>
          </Carousel>
        </div>
        <!-- サムネイルエリア (縦スクロール) -->
        <div class="relative w-1/5 self-stretch">
          <div ref="thumbnailScrollRef" class="absolute inset-0 flex flex-col gap-y-[2px] overflow-y-auto">
            <div v-for="(imageUrl, index) in imgUrls" :key="index" :ref="(el) => setThumbnailRef(el as HTMLElement, index)" class="shrink-0 cursor-pointer" @click="onThumbClick(index)">
              <OptimizedImage
                loading="lazy"
                :alt="`experience-thumbnail-${index}`"
                :class="['aspect-[3/2] w-full object-cover object-center', selectedIndex === index ? 'opacity-30' : '']"
                :image-url="imageUrl"
                :optimized-image-options="{ width: 320 }"
              />
            </div>
          </div>
          <!-- 下矢印インジケーター (スクロール可能で、まだ下がある場合) -->
          <div
            v-if="showScrollDownIndicator"
            class="absolute bottom-2 left-1/2 flex h-6 w-6 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-black/50 transition-colors hover:bg-black/70"
            @click="onClickScrollDown"
          >
            <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
      </div>
      <!-- SSR Fallback: 最初の画像を表示 -->
      <template #fallback>
        <div class="flex gap-x-[2px]">
          <div class="w-full md:w-4/5">
            <OptimizedImage
              v-if="imgUrls.length > 0"
              loading="lazy"
              :alt="`experience-image-0`"
              :class="['aspect-[3/2] w-full object-cover object-center']"
              :image-url="imgUrls[0]"
              :optimized-image-options="{ width: 1280 }"
            />
          </div>
          <div class="hidden md:block md:w-1/5" />
        </div>
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useElementSize, useScroll } from '@vueuse/core';

import type { CarouselApi } from '@/components/lib/shadcn-vue/carousel';
import CarouselNextBase from '@/components/lib/shadcn-vue/carousel/CarouselNextBase.vue';
import CarouselPreviousBase from '@/components/lib/shadcn-vue/carousel/CarouselPreviousBase.vue';
import { useIsSP } from '@/composables/ui/useIsSP';
import { OptimizedImage } from '@/wabunka-src/common/components';

import type { VNodeRef } from 'vue';

interface Props {
  imgUrls: string[];
  onClickExpand: (e: MouseEvent, index: number) => void;
}

const props = defineProps<Props>();

const isSP = useIsSP();

const isCarouselScrolling = ref(false);

const desktopCarouselRef = ref<VNodeRef | null>(null);
const thumbnailScrollRef = ref<HTMLElement | null>(null);
const { width: desktopCarouselElementWidth } = useElementSize(desktopCarouselRef);
const { arrivedState } = useScroll(thumbnailScrollRef);

const showScrollDownIndicator = computed(() => {
  if (props.imgUrls.length <= 4) return false;
  return !arrivedState.bottom;
});

const emblaMainApi = ref<CarouselApi>();
const emblaThumbnailApi = ref<CarouselApi>();
const selectedIndex = ref(0);
const thumbnailRefs = ref<(HTMLElement | null)[]>([]);

const setThumbnailRef = (el: HTMLElement | null, index: number) => {
  thumbnailRefs.value[index] = el;
};

const scrollThumbnailIntoView = (index: number) => {
  const container = thumbnailScrollRef.value;
  if (!container) return;

  // 最初の画像は一番上にスクロール
  if (index === 0) {
    container.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  const el = thumbnailRefs.value[index];
  if (!el) return;

  const containerHeight = container.clientHeight;
  const elTop = el.offsetTop;
  const elHeight = el.clientHeight;
  const scrollTo = elTop - containerHeight / 2 + elHeight / 2;

  container.scrollTo({ top: scrollTo, behavior: 'smooth' });
};

const handleMainCarouselClick = (e: MouseEvent) => {
  if (isCarouselScrolling.value || !emblaMainApi.value) {
    return;
  }

  const width = isSP.value ? (e.currentTarget as HTMLElement).offsetWidth : desktopCarouselElementWidth.value;
  if (e.offsetX < width / 2) {
    emblaMainApi.value.scrollPrev();
    return;
  }

  emblaMainApi.value.scrollNext();
};

const canScrollPrev = computed(() => selectedIndex.value !== 0);
const canScrollNext = computed(() => selectedIndex.value + 1 < props.imgUrls.length);

const onClickScrollPrev = (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  emblaMainApi.value?.scrollPrev();
};
const onClickScrollNext = (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  emblaMainApi.value?.scrollNext();
};

const onSelect = () => {
  if (!emblaMainApi.value) return;

  selectedIndex.value = emblaMainApi.value.selectedScrollSnap();

  if (isSP.value) {
    // SP: サムネイルカルーセルをスクロール
    if (emblaThumbnailApi.value) {
      emblaThumbnailApi.value.scrollTo(selectedIndex.value);
    }
  } else {
    // Desktop: サムネイルコンテナをスクロール
    scrollThumbnailIntoView(selectedIndex.value);
  }
};

const onThumbClick = (index: number) => {
  emblaMainApi.value?.scrollTo(index);
};

const onClickScrollDown = () => {
  emblaMainApi.value?.scrollNext();
};

const handleExpandClick = (e: MouseEvent) => {
  e.stopPropagation();
  props.onClickExpand(e, selectedIndex.value);
};

// イベントハンドラ（クリーンアップのため名前付き関数として定義）
// スクロール中の状態を管理（select時にtrue、settle時にfalse）
// ref: https://www.embla-carousel.com/api/events/#scroll
// NOTE: settle eventが発火されるまでの時間が長いので考えどころ
const onSelectHandler = () => {
  isCarouselScrolling.value = true;
  onSelect();
};
const onSettleHandler = () => {
  isCarouselScrolling.value = false;
};

// カルーセル初期化
watch(
  emblaMainApi,
  (api, oldApi) => {
    // 古いリスナーをクリーンアップ
    if (oldApi) {
      oldApi.off('select', onSelectHandler);
      oldApi.off('reInit', onSelect);
      oldApi.off('settle', onSettleHandler);
    }

    if (!api) return;

    // リサイズ等で再初期化された場合、保存されたインデックスに移動
    if (selectedIndex.value > 0) {
      api.scrollTo(selectedIndex.value, true);
    }

    onSelect();
    api.on('select', onSelectHandler);
    api.on('reInit', onSelect);
    api.on('settle', onSettleHandler);
  },
  { immediate: true },
);

// コンポーネントアンマウント時にイベントリスナーをクリーンアップ
onBeforeUnmount(() => {
  if (emblaMainApi.value) {
    emblaMainApi.value.off('select', onSelectHandler);
    emblaMainApi.value.off('reInit', onSelect);
    emblaMainApi.value.off('settle', onSettleHandler);
  }
});

// 初期表示時にサムネイルスクロールを一番上にリセット
onMounted(() => {
  nextTick(() => {
    if (thumbnailScrollRef.value) {
      thumbnailScrollRef.value.scrollTop = 0;
    }
  });
});
</script>
